import AuthService from 'app/services/core/login.service'
import EnrollmentService from 'app/services/enrollment/enrollment.service'
import { AmpConfig } from 'app/types/amp-config.types'
import { PrechatFields } from 'app/types/prechat-fields.types'
import { User } from 'app/types/auth.types'

let hasAlreadyInitialized: boolean

const hasAuthorizedRole = (): boolean => {
  const userRoles = AuthService.getRoles()
  return (
    userRoles.includes('TEACHER') &&
    !userRoles.includes('READ_ONLY') &&
    !userRoles.includes('REVIEWER')
  )
}

const salesforceChatEnabled = (ampConfig: AmpConfig): boolean =>
  !!ampConfig?.flags?.isSalesforceChatEnabled && hasAuthorizedRole()

const loadSalesforceScript = (ampConfig: AmpConfig) =>
  new Promise<void>((resolve, reject) => {
    try {
      const { scriptSrc = '' } = ampConfig?.salesforceChat || {}

      const script = document.createElement('script')
      script.src = scriptSrc
      script.async = true
      // Callback function to run when script is loaded
      script.onload = () => resolve()
      document.body.appendChild(script)
    } catch (error) {
      reject(error)
    }
  })

const getIPFromAmazon = () =>
  fetch('https://checkip.amazonaws.com/')
    .then((response) => response.text())
    .then((ip) => ip.trim())
    .then((ip) => ip.trim())
    .catch((error) => console.error(error))

const formatField = (val: string | string[]) => `${val}`.substring(0, 100)

const getPrechatFields = (): Promise<PrechatFields> => {
  const token = AuthService.getAccessToken()
  const userKey = AuthService.getUserBk()

  return Promise.all([
    EnrollmentService.getUserEnrollment(token, userKey),
    getIPFromAmazon(),
  ]).then(([userEnrollment, ipAddress = '']) => {
    const user = userEnrollment.user as User
    const { email, givenName, familyName, sourcedId } = user
    const districts = user.orgs.filter((org) => org.orgType === 'district')
    const schools = user.orgs.filter((org) => org.orgType === 'school')

    const districtIds = districts.map((distr) => distr.sourcedId)
    const districtNames = districts.map((distr) => distr.name)

    const schoolIds = schools.map((distr) => distr.sourcedId)
    const schoolNames = schools.map((distr) => distr.name)

    const referralUrl = window.location.href
    return {
      'email': formatField(email),
      'firstName': formatField(givenName),
      'lastName': formatField(familyName),
      'referral_url': formatField(referralUrl),
      'ip_address': formatField(ipAddress),
      'products': 'CKLA Hub',
      'amplify_id': formatField(sourcedId),
      'school_id': formatField(schoolIds),
      'School_id name': formatField(schoolNames),
      'district_id': formatField(districtIds),
      'district': formatField(districtNames),
    }
  })
}

const setIdentityToken = () => {
  const token = AuthService.getAccessToken()
  // Send your identity token to Salesforce
  window.embeddedservice_bootstrap.userVerificationAPI.setIdentityToken({
    identityTokenType: 'JWT',
    identityToken: token,
  })
}

const setHiddenPrechatFields = async () => {
  const prechatFields = await getPrechatFields()
  window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields(
    prechatFields
  )
}

const handleSalesForceScriptLoaded = () => {
  setIdentityToken()
  setHiddenPrechatFields()
}

const initSalesforceChat = (ampConfig: AmpConfig) => {
  try {
    const { salesforceOrgId, chatBotLabel, chatBotAddress, salesforceOrgCert } =
      ampConfig?.salesforceChat || {}

    window.embeddedservice_bootstrap.settings.language = 'en_US' // For example, enter 'en' or 'en_US'
    window.embeddedservice_bootstrap.init(
      salesforceOrgId,
      chatBotLabel,
      chatBotAddress,
      {
        scrt2URL: salesforceOrgCert,
      }
    )

    window.addEventListener(
      'onEmbeddedMessagingReady',
      handleSalesForceScriptLoaded
    )
    window.addEventListener(
      'onEmbeddedMessagingIdentityTokenExpired',
      setIdentityToken
    )

    hasAlreadyInitialized = true
  } catch (error) {
    console.error(error)
  }
}

const init = (ampConfig: AmpConfig) => {
  const salesforceChatIsEnabled = salesforceChatEnabled(ampConfig)
  if (!salesforceChatIsEnabled || hasAlreadyInitialized) {
    return
  }

  loadSalesforceScript(ampConfig)
    .then(() => initSalesforceChat(ampConfig))
    .catch((error) => console.error(error))
}

const cleanup = () => {
  window.removeEventListener(
    'onEmbeddedMessagingReady',
    handleSalesForceScriptLoaded
  )
  window.removeEventListener(
    'onEmbeddedMessagingIdentityTokenExpired',
    setIdentityToken
  )
}

const SalesforceChatService = {
  init,
  cleanup,
}

export default SalesforceChatService
